import React, { useEffect, useState } from "react";
import "./Addcarryforward.css";
import apiClient from "../utils/apiClient";
import SessionHandler from "../components/SessionHandler";

const CarryForwardLeave = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [cfData, setCfData] = useState({});

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const [nameFilter, setNameFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("success");

  useEffect(() => {
    fetchEmployeesAndCF();
  }, []);

  /* ================= FETCH EMPLOYEES + CF ================= */
  const fetchEmployeesAndCF = async () => {
    try {
      // 1️⃣ Employees
      const empRes = await apiClient("/auth/allemployees/");
      const empList = Array.isArray(empRes.results)
        ? empRes.results
        : empRes;

      // 2️⃣ CF allocations (ARRAY)
      const cfRes = await apiClient("/cfleaves/");
      const cfList = Array.isArray(cfRes) ? cfRes : [];

      // 3️⃣ Convert CF list → MAP { employee_id: leave }
      const cfMap = {};
      cfList.forEach((cf) => {
        cfMap[cf.employee_id] = cf.carry_forward_leave;
      });

      // 4️⃣ Pre-fill CF values
      const initialCF = {};
      empList.forEach((emp) => {
        initialCF[emp.id] = cfMap[emp.id] ?? "";
      });

      setEmployees(empList);
      setFilteredEmployees(empList);
      setCfData(initialCF);
    } catch (err) {
      console.error("Error fetching employees or CF leaves", err);
    }
  };

  /* ================= FILTERS ================= */
  useEffect(() => {
    let filtered = [...employees];

    if (nameFilter) {
      filtered = filtered.filter((e) =>
        e.name?.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    if (departmentFilter) {
      filtered = filtered.filter(
        (e) =>
          e.department?.toLowerCase() === departmentFilter.toLowerCase()
      );
    }

    setFilteredEmployees(filtered);
  }, [nameFilter, departmentFilter, employees]);

  /* ================= INPUT CHANGE ================= */
  const handleChange = (empId, value) => {
    setCfData((prev) => ({
      ...prev,
      [empId]: value,
    }));
  };

  /* ================= SAVE ================= */
  const handleSaveAll = async () => {
    const payload = Object.entries(cfData).map(([empId, value]) => ({
      employee_id: Number(empId),
      carry_forward_leave: Number(value || 0),
    }));

    try {
      setLoading(true);

      await apiClient("/cfleaves/", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setModalType("success");
      setModalMessage("Carry Forward Leave saved successfully");
      setShowModal(true);

      setIsEditing(false);
      fetchEmployeesAndCF();
    } catch (err) {
      console.error(err);
      setModalType("error");
      setModalMessage("Failed to save Carry Forward Leave");
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    fetchEmployeesAndCF();
  };

  const departmentOptions = [
    ...new Set(employees.map((e) => e.department).filter(Boolean)),
  ];

  return (
    <div className="cf-container">
      <h2 className="cf-title">Carry Forward Leave Management</h2>

      {/* FILTERS */}
      <div className="cf-filters">
        <input
          type="text"
          placeholder="Search by Name"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
        />

        <select
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
        >
          <option value="">All Departments</option>
          {departmentOptions.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>
      </div>

      {/* TABLE */}
      <table className="cf-table">
        <thead>
          <tr>
            <th>Employee Name</th>
            <th>Employee ID</th>
            <th>Department</th>
            <th>CF Leave</th>
          </tr>
        </thead>

        <tbody>
          {filteredEmployees.map((emp) => (
            <tr key={emp.id}>
              <td>{emp.name}</td>
              <td>{emp.employee_id}</td>
              <td>{emp.department || "—"}</td>
              <td>
                <input
                  type="number"
                  min="0"
                  disabled={!isEditing}
                  value={cfData[emp.id] ?? ""}
                  onChange={(e) =>
                    handleChange(emp.id, e.target.value)
                  }
                />
              </td>
            </tr>
          ))}

          {filteredEmployees.length === 0 && (
            <tr>
              <td colSpan="4" style={{ textAlign: "center" }}>
                No employees found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ACTIONS */}
      <div className="cf-actions">
        {!isEditing ? (
          <button className="btn-edit" onClick={() => setIsEditing(true)}>
            Edit
          </button>
        ) : (
          <>
            <button
              className="btn-save"
              disabled={loading}
              onClick={handleSaveAll}
            >
              {loading ? "Saving..." : "Save"}
            </button>
            <button className="btn-cancel" onClick={handleCancel}>
              Cancel
            </button>
          </>
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="cf-modal-overlay">
          <div className={`cf-modal ${modalType}`}>
            <h3>{modalType === "success" ? "Success" : "Error"}</h3>
            <p>{modalMessage}</p>
            <button onClick={() => setShowModal(false)}>OK</button>
          </div>
        </div>
      )}

      <SessionHandler />
    </div>
  );
};

export default CarryForwardLeave;
